import { useEffect, memo } from 'react';
import Raven from 'raven-js';
import { reportError } from 'conversations-error-reporting/error-reporting/reportError';
import { isLoading, isFailed } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import AgentThreadHistory from '../../thread-history/components/AgentThreadHistory';
import MessageHistoryContext from 'conversations-inbox-message-history/MessageHistoryContext';
import { ThreadHistoryWrapper, LegacyThreadHistoryWrapper } from './ThreadHistoryWrapper';
import ThreadHistorySkeletonState from './ThreadHistorySkeletonState';
import ThreadHistoryError from '../../email/components/ThreadHistoryError';
// @ts-expect-error module not typed
import { hasMessages } from 'conversations-message-history/thread-history/operators/hasMessages';
import { NavMarker } from 'react-rhumb';
import { THREAD_LIST_SWITCHING_MARKER, THREAD_SWITCHING_MARKER, CRITICAL_PATH_THREAD_HISTORY, SECONDARY_PATH_THREAD_HISTORY } from '../../performance-metrics/constants/performanceConstants';
import { getAdditionalPositionDescriptors } from '../operators/getAdditionalPositionDescriptors';
import { Metrics } from '../../common/Metrics';
import { useSelector } from 'react-redux';
import { getIsUngatedForThreadMessagesUIImprovements } from 'conversations-thread-data/auth/public/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
export const checksumTimeoutLength = 5000;
export const createCaptureOnceForKey = () => {
  const captured = {};
  return (threadId, trackFn) => {
    /* eslint-disable no-prototype-builtins */
    if (!captured.hasOwnProperty(threadId)) {
      captured[threadId] = true;
      trackFn();
    }
  };
};
function usePerformanceTimer(asyncThreadHistory, vid, threadId, threadHistoryRequestFailed, threadPerformanceMeasureOnceForThreadId) {
  if (!isLoading(asyncThreadHistory) && hasMessages(getData(asyncThreadHistory)) && vid && !threadHistoryRequestFailed) {
    threadPerformanceMeasureOnceForThreadId(threadId, () => {
      try {
        const startEntries = performance.getEntriesByName(`${THREAD_SWITCHING_MARKER}-start-${threadId}`);
        if (startEntries && startEntries.length > 0) {
          performance.mark(`conversations-thread-list-row-stop-${threadId}`);
          performance.measure(`${THREAD_SWITCHING_MARKER}-switch-${threadId}`, `${THREAD_SWITCHING_MARKER}-start-${threadId}`, `${THREAD_SWITCHING_MARKER}-stop-${threadId}`);
          const duration = performance.getEntriesByName(`${THREAD_SWITCHING_MARKER}-switch-${threadId}`)[0].duration;
          Raven.setExtraContext({
            [`${THREAD_SWITCHING_MARKER}-switch`]: duration
          });
          Metrics.timer('thread-load-after-thread-switch').update(Math.round(duration));
        } else {
          const navigationEntry = performance.getEntriesByType('navigation')[0];
          const pageReceivedMarker =
          // @ts-expect-error ts-migration
          navigationEntry && navigationEntry.responseEnd;
          const threadLoadMarker = 'THREAD_LOAD_MARKER';
          performance.mark(threadLoadMarker);
          const duration = performance.now() - pageReceivedMarker;
          performance.clearMarks(threadLoadMarker);
          Metrics.timer('thread-load-after-page-load').update(Math.round(duration));
        }

        //Capture data regarding how long it takes for message history to load
        //when the user switches thread lists. Limit to once per new thread ID
        //so that we aren't measuring the duration when a thread in the
        //destination thread list is already cached, which would artificially
        //"improve" our metrics.
        const threadListSwitchEntries = performance.getEntriesByName(`${THREAD_LIST_SWITCHING_MARKER}-start`);
        if (threadListSwitchEntries && threadListSwitchEntries.length) {
          performance.mark(`${THREAD_LIST_SWITCHING_MARKER}-stop`);
          performance.measure(`${THREAD_LIST_SWITCHING_MARKER}-switch`, `${THREAD_LIST_SWITCHING_MARKER}-start`, `${THREAD_LIST_SWITCHING_MARKER}-stop`);
          const duration = performance.getEntriesByName(`${THREAD_LIST_SWITCHING_MARKER}-switch`)[0].duration;
          Raven.setExtraContext({
            [`${THREAD_LIST_SWITCHING_MARKER}-switch`]: duration
          });
        }
      } catch (error) {
        reportError({
          // @ts-expect-error ts-migration
          error,
          extra: {
            threadId,
            message: 'error recording performance metrics for thread switching '
          }
        });
      }
    });
  }
}
const UnifiedThreadHistory = ({
  allowContactPromotions,
  AsyncUIAvatar,
  asyncThreadHistory,
  canRestoreMessagesForThread,
  currentUserId,
  fetchAgentHistory,
  historyConfig,
  incomingThreadId,
  messagesList,
  portalId,
  threadHistoryRequestFailed,
  threadId,
  vid,
  slots
}) => {
  const ungatedForThreadMessagesUIImprovements = useSelector(getIsUngatedForThreadMessagesUIImprovements);
  const threadPerformanceMeasureOnceForThreadId = createCaptureOnceForKey();

  // to be run once to capture performance metrics
  useEffect(() => {
    try {
      performance.measure(CRITICAL_PATH_THREAD_HISTORY);
      const entries = performance.getEntriesByName(CRITICAL_PATH_THREAD_HISTORY);
      if (entries && entries.length > 0) {
        const duration = entries[0].duration;
        Raven.setExtraContext({
          [`${incomingThreadId ? CRITICAL_PATH_THREAD_HISTORY : SECONDARY_PATH_THREAD_HISTORY}`]: duration
        });
      }
    } catch (error) {
      reportError({
        // @ts-expect-error ts-migration
        error,
        extra: {
          threadId: incomingThreadId,
          message: 'error recording performance metrics thread history critical path '
        }
      });
    }
  });
  usePerformanceTimer(asyncThreadHistory, vid, threadId, threadHistoryRequestFailed, threadPerformanceMeasureOnceForThreadId);
  const renderThreadHistoryStatusWrapper = () => {
    const threadHistory = getData(asyncThreadHistory);
    if (isLoading(asyncThreadHistory) && !hasMessages(threadHistory) || !vid) {
      return /*#__PURE__*/_jsx(ThreadHistorySkeletonState, {});
    }
    if (isFailed(asyncThreadHistory) && !hasMessages(threadHistory)) {
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "THREAD_LIST_ERROR",
        children: /*#__PURE__*/_jsx(ThreadHistoryError, {})
      });
    }
    return /*#__PURE__*/_jsx(MessageHistoryContext.Provider, {
      value: {
        AvatarComponent: AsyncUIAvatar
      },
      children: /*#__PURE__*/_jsx(AgentThreadHistory, {
        allowContactPromotions: allowContactPromotions,
        canRestoreMessagesForThread: canRestoreMessagesForThread,
        currentUserId: currentUserId
        // @ts-expect-error ts-migration
        ,
        fetchHistory: fetchAgentHistory,
        getAdditionalPositionDescriptors: getAdditionalPositionDescriptors,
        historyConfig: historyConfig,
        messages: messagesList,
        portalId: portalId,
        threadHistory: getData(asyncThreadHistory),
        threadId: threadId,
        vid: vid,
        slots: slots,
        ungatedForThreadMessagesUIImprovements: ungatedForThreadMessagesUIImprovements
      })
    });
  };
  const HistoryWrapper = ungatedForThreadMessagesUIImprovements ? ThreadHistoryWrapper : LegacyThreadHistoryWrapper;
  return /*#__PURE__*/_jsx(HistoryWrapper, {
    children: renderThreadHistoryStatusWrapper()
  });
};
UnifiedThreadHistory.displayName = 'UnifiedThreadHistory';
export default /*#__PURE__*/memo(UnifiedThreadHistory);